import { useState, useCallback, useEffect, useMemo } from 'react'
import { useApiFetch } from '../hooks/useApiFetch'
import { useApiMutation } from '../hooks/useApiMutation'
import { useToast } from '../components/ToastContainer'
import { apiFetch } from '../lib/api'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Select } from '../ui/Select'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Spinner } from '../ui/Spinner'
import { AdminTable } from '../ui/AdminTable'
import { FileUpload } from '../ui/FileUpload'
import type { TableColumn } from '../ui/Table'
import styles from './Fonts.module.css'

// ── 타입 ──

interface FontVariant {
  weight: number
  style: 'normal' | 'italic'
  s3Key?: string
  url?: string
  format?: 'woff2' | 'woff' | 'ttf' | 'otf'
  fileName?: string // UI 표시용
}

interface Font {
  id: string
  name: string
  slug: string
  sourceType: 'google' | 'custom'
  cssUrl: string | null
  fontFamily: string
  category: string
  variants: FontVariant[]
  previewText: string | null
  isBuiltin: boolean
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

interface FontsResponse {
  fonts: Font[]
}

const SOURCE_TYPE_OPTIONS = [
  { value: 'google', label: 'Google Fonts' },
  { value: 'custom', label: '커스텀' },
]

const CATEGORY_OPTIONS = [
  { value: 'sans', label: 'Sans-serif' },
  { value: 'serif', label: 'Serif' },
  { value: 'display', label: 'Display' },
  { value: 'handwriting', label: '손글씨' },
  { value: 'monospace', label: 'Monospace' },
]

const CATEGORY_BADGE_COLORS: Record<string, 'blue' | 'green' | 'red' | 'yellow'> = {
  sans: 'blue',
  serif: 'green',
  display: 'yellow',
  handwriting: 'red',
  monospace: 'blue',
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9가-힣\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 64)
}

// Helper to inject Google Font link for preview
function useGoogleFontPreview(cssUrl: string | null | undefined) {
  useEffect(() => {
    if (!cssUrl) return
    const id = `font-preview-${btoa(cssUrl).slice(0, 20)}`
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = cssUrl
    document.head.appendChild(link)
  }, [cssUrl])
}

// Helper to inject @font-face for custom font variants preview
function useCustomFontPreview(fontFamily: string, variants: FontVariant[]) {
  useEffect(() => {
    if (!fontFamily || variants.length === 0) return
    const familyName = fontFamily.replace(/['"]/g, '').split(',')[0].trim()
    const id = `custom-font-preview-${familyName.replace(/\s+/g, '-')}`
    let style = document.getElementById(id) as HTMLStyleElement | null
    if (!style) {
      style = document.createElement('style')
      style.id = id
      document.head.appendChild(style)
    }
    const faces = variants
      .filter((v) => v.url)
      .map((v) => {
        const format = v.format === 'woff2' ? 'woff2' : v.format === 'woff' ? 'woff' : v.format === 'otf' ? 'opentype' : 'truetype'
        return `@font-face {
  font-family: '${familyName}';
  font-weight: ${v.weight};
  font-style: ${v.style};
  src: url('${v.url}') format('${format}');
  font-display: swap;
}`
      })
      .join('\n')
    style.textContent = faces
    return () => {
      style?.remove()
    }
  }, [fontFamily, variants])
}

// ── 메인 컴포넌트 ──

export function Fonts() {
  const { addToast } = useToast()

  // 폰트 목록
  const { data, isLoading, refetch } = useApiFetch<FontsResponse>('/api/admin/fonts?includeInactive=true')
  const fontsList = data?.fonts ?? []

  // 모달 상태
  const [modalOpen, setModalOpen] = useState(false)
  const [editingFont, setEditingFont] = useState<Font | null>(null)

  // 폼 상태
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugManual, setSlugManual] = useState(false)
  const [sourceType, setSourceType] = useState<'google' | 'custom'>('google')
  const [cssUrl, setCssUrl] = useState('')
  const [fontFamily, setFontFamily] = useState('')
  const [category, setCategory] = useState('sans')
  const [sortOrder, setSortOrder] = useState(0)
  const [previewText, setPreviewText] = useState('')
  const [variants, setVariants] = useState<FontVariant[]>([])
  const [quickUploading, setQuickUploading] = useState(false)

  // 뮤테이션
  const createMut = useApiMutation<Font, Record<string, unknown>>('/api/admin/fonts', 'POST')
  const updateMut = useApiMutation<Font, Record<string, unknown>>('/api/admin/fonts', 'PUT')
  const toggleMut = useApiMutation<unknown, Record<string, unknown>>('/api/admin/fonts', 'PATCH')

  // 목록의 폰트 로드 (Google + Custom)
  useEffect(() => {
    for (const font of fontsList) {
      if (font.sourceType === 'google' && font.cssUrl) {
        const id = `font-list-${font.slug}`
        if (document.getElementById(id)) continue
        const link = document.createElement('link')
        link.id = id
        link.rel = 'stylesheet'
        link.href = font.cssUrl
        document.head.appendChild(link)
      } else if (font.sourceType === 'custom' && font.variants?.length > 0) {
        const id = `font-list-custom-${font.slug}`
        if (document.getElementById(id)) continue
        const familyName = font.fontFamily.replace(/['"]/g, '').split(',')[0].trim()
        const style = document.createElement('style')
        style.id = id
        style.textContent = font.variants
          .filter((v) => v.url)
          .map((v) => {
            const format = v.format === 'woff2' ? 'woff2' : v.format === 'woff' ? 'woff' : v.format === 'otf' ? 'opentype' : 'truetype'
            return `@font-face { font-family: '${familyName}'; font-weight: ${v.weight}; font-style: ${v.style}; src: url('${v.url}') format('${format}'); font-display: swap; }`
          })
          .join('\n')
        document.head.appendChild(style)
      }
    }
  }, [fontsList])

  // 모달 프리뷰용 폰트 로드
  useGoogleFontPreview(sourceType === 'google' ? cssUrl : null)
  useCustomFontPreview(sourceType === 'custom' ? fontFamily : '', variants)

  const resetForm = useCallback(() => {
    setName('')
    setSlug('')
    setSlugManual(false)
    setSourceType('google')
    setCssUrl('')
    setFontFamily('')
    setCategory('sans')
    setSortOrder(0)
    setPreviewText('')
    setVariants([])
    setEditingFont(null)
  }, [])

  const openGoogleFonts = useCallback(() => {
    resetForm()
    setSourceType('google')
    setModalOpen(true)
  }, [resetForm])

  const handleQuickUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return
    setQuickUploading(true)
    try {
      for (const file of files) {
        try {
          const formData = new FormData()
          formData.append('file', file)
          const result = await apiFetch<{
            font: Font
            parsed: { fontFamily: string; displayName: string; weight: number; style: 'normal' | 'italic'; suggestedSlug: string; suggestedCategory: string }
            isNewFont: boolean
          }>('/api/admin/fonts/upload', { method: 'POST', body: formData })
          addToast({
            type: 'success',
            message: `"${result.parsed.displayName}" 폰트가 ${result.isNewFont ? '추가' : '업데이트'}되었습니다`,
          })
        } catch (err: any) {
          addToast({ type: 'error', message: `${file.name}: ${err.message || '업로드 실패'}` })
        }
      }
    } finally {
      setQuickUploading(false)
      refetch()
    }
  }, [addToast, refetch])

  const openEdit = useCallback((font: Font) => {
    setEditingFont(font)
    setName(font.name)
    setSlug(font.slug)
    setSlugManual(true)
    setSourceType(font.sourceType)
    setCssUrl(font.cssUrl ?? '')
    setFontFamily(font.fontFamily)
    setCategory(font.category)
    setSortOrder(font.sortOrder)
    setPreviewText(font.previewText ?? '')
    setVariants(font.variants ?? [])
    setModalOpen(true)
  }, [])

  const handleNameChange = useCallback((val: string) => {
    setName(val)
    if (!slugManual) setSlug(toSlug(val))
  }, [slugManual])

  const handleSave = useCallback(async () => {
    const payload = {
      name,
      slug,
      sourceType,
      cssUrl: sourceType === 'google' ? cssUrl || null : null,
      fontFamily,
      category,
      sortOrder,
      previewText: previewText || null,
      variants: sourceType === 'custom' ? variants.map(({ fileName, ...v }) => v) : [],
    }

    if (editingFont) {
      const result = await updateMut.trigger(payload, {
        pathOverride: `/api/admin/fonts/${editingFont.id}`,
      })
      if (result) {
        addToast({ type: 'success', message: '폰트가 수정되었습니다' })
        setModalOpen(false)
        resetForm()
        refetch()
      }
    } else {
      const result = await createMut.trigger(payload)
      if (result) {
        addToast({ type: 'success', message: '폰트가 생성되었습니다' })
        setModalOpen(false)
        resetForm()
        refetch()
      }
    }
  }, [name, slug, sourceType, cssUrl, fontFamily, category, sortOrder, previewText, variants, editingFont, updateMut, createMut, addToast, resetForm, refetch])

  const handleToggle = useCallback(
    async (row: Font, newValue: boolean) => {
      const result = await toggleMut.trigger(
        { isActive: newValue },
        { pathOverride: `/api/admin/fonts/${row.id}/toggle` },
      )
      if (result === null) throw new Error('토글 변경에 실패했습니다')
      refetch()
    },
    [toggleMut, refetch],
  )

  const handleDelete = useCallback(
    async (row: Font) => {
      await apiFetch(`/api/admin/fonts/${row.id}`, { method: 'DELETE' })
      refetch()
    },
    [refetch],
  )

  // ── 렌더 ──

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px' }}>
        <Spinner />
      </div>
    )
  }

  const columns: TableColumn<Font>[] = [
    {
      key: 'sortOrder',
      label: '순서',
      width: '60px',
    },
    {
      key: 'name',
      label: '이름',
      render: (row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.name}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>{row.slug}</div>
        </>
      ),
    },
    {
      key: 'sourceType',
      label: '소스',
      width: '100px',
      render: (row) => (
        <Badge color={row.sourceType === 'google' ? 'blue' : 'yellow'}>
          {row.sourceType === 'google' ? 'Google' : '커스텀'}
        </Badge>
      ),
    },
    {
      key: 'category',
      label: '카테고리',
      width: '110px',
      render: (row) => (
        <Badge color={CATEGORY_BADGE_COLORS[row.category] ?? 'blue'}>
          {row.category}
        </Badge>
      ),
    },
    {
      key: 'preview',
      label: '프리뷰',
      render: (row) => (
        <div className={styles.fontPreview} style={{ fontFamily: row.fontFamily }}>
          {row.previewText || '가나다라 ABC 123'}
        </div>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
      }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>폰트 관리</h2>
        <Button variant="secondary" onClick={openGoogleFonts}>Google Fonts 추가</Button>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <FileUpload
          accept=".woff2,.woff,.ttf,.otf"
          multiple
          maxFiles={20}
          placeholder=".woff2, .woff, .ttf, .otf 파일을 드래그하거나 클릭 (자동 등록)"
          onChange={(files) => handleQuickUpload(files)}
          loading={quickUploading}
        />
      </div>

      <AdminTable<Font>
        columns={columns}
        data={fontsList}
        rowKey={row => row.id}
        emptyMessage="등록된 폰트가 없습니다"
        hoverable
        toggles={[
          {
            label: '활성',
            onToggle: handleToggle,
            getValue: (row) => row.isActive,
          },
        ]}
        actions={[
          {
            label: '편집',
            onClick: (row) => openEdit(row),
          },
        ]}
        delete={{
          message: (row) => `"${row.name}" 폰트를 삭제하시겠습니까?`,
          onDelete: handleDelete,
          hidden: (row) => row.isBuiltin,
          successMessage: '폰트가 삭제되었습니다',
        }}
      />

      {/* 생성/수정 모달 */}
      <Modal
        open={modalOpen}
        onClose={() => { setModalOpen(false); resetForm() }}
        title={editingFont ? '폰트 편집' : 'Google Fonts 추가'}
        size="lg"
      >
        <div className={styles.formGrid}>
          <Input
            label="이름"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Noto Sans KR"
          />
          <Input
            label="Slug"
            value={slug}
            onChange={(e) => { setSlug(e.target.value); setSlugManual(true) }}
            placeholder="noto-sans"
          />
          <Select
            label="소스 타입"
            value={sourceType}
            onChange={(e) => setSourceType(e.target.value as 'google' | 'custom')}
            options={SOURCE_TYPE_OPTIONS}
            disabled={!!editingFont}
          />
          <Select
            label="카테고리"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            options={CATEGORY_OPTIONS}
          />
          {sourceType === 'google' && (
            <div className={styles.formFull}>
              <Input
                label="CSS URL"
                value={cssUrl}
                onChange={(e) => setCssUrl(e.target.value)}
                placeholder="https://fonts.googleapis.com/css2?family=..."
              />
            </div>
          )}
          <div className={styles.formFull}>
            <Input
              label="font-family CSS 값"
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              placeholder="'Noto Sans KR', sans-serif"
            />
          </div>
          <Input
            label="정렬 순서"
            type="number"
            value={String(sortOrder)}
            onChange={(e) => setSortOrder(Number(e.target.value))}
          />
          <Input
            label="프리뷰 텍스트"
            value={previewText}
            onChange={(e) => setPreviewText(e.target.value)}
            placeholder="가나다라 ABC 123"
          />

          {/* 모달 내 프리뷰 */}
          {fontFamily && (
            <div className={styles.formFull}>
              <div style={{ padding: '16px', background: '#f9fafb', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>프리뷰</div>
                <div style={{ fontFamily, fontSize: 20 }}>
                  {previewText || '가나다라마바사 아자차카타파하'}
                </div>
                <div style={{ fontFamily, fontSize: 14, marginTop: 4 }}>
                  The quick brown fox jumps over the lazy dog. 0123456789
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button variant="secondary" onClick={() => { setModalOpen(false); resetForm() }}>
            취소
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name || !slug || !fontFamily || createMut.isLoading || updateMut.isLoading}
          >
            {editingFont ? '수정' : '생성'}
          </Button>
        </div>
      </Modal>
    </div>
  )
}
